
import React, { useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Estimate } from "@/types/project-types";
import { ComponentType } from "@/types/estimation-types";
import { componentTypes } from "@/data/estimationData";
import EstimationSummary from "@/components/rfp/EstimationSummary";
import EstimationTable from "@/components/rfp/EstimationTable";
import EstimationNotes from "@/components/rfp/EstimationNotes";
import ProjectActions from "./ProjectActions";

interface EstimationTabContentProps {
  estimates: Estimate[];
  project: {
    id: string;
    name: string;
    description?: string;
    version: number;
    updated_at: string;
  };
  isSaving: boolean;
  isSendingEmail: boolean;
  rfpEstimates: any[];
  totals: any;
  onEstimateUpdate: (index: number, field: string, value: number) => void;
  onSaveEstimates: () => Promise<void>;
  onSendEmail: () => Promise<void>;
  onGeneratePDF: () => void;
  onExportExcel: () => void;
}

const EstimationTabContent: React.FC<EstimationTabContentProps> = ({
  estimates,
  project,
  isSaving,
  isSendingEmail,
  rfpEstimates,
  totals,
  onEstimateUpdate,
  onSaveEstimates,
  onSendEmail,
  onGeneratePDF,
  onExportExcel
}) => {
  const reportRef = useRef<HTMLDivElement>(null);
  
  const sortedEstimates = [...estimates].sort((a, b) => {
    const aIndex = componentTypes.indexOf(a.component_type as ComponentType);
    const bIndex = componentTypes.indexOf(b.component_type as ComponentType);
    return aIndex - bIndex;
  });

  return (
    <div className="space-y-4 pt-4">
      <Card className="bg-white dark:bg-gray-900 border-purple-100 dark:border-gray-800">
        <CardHeader className="border-b border-purple-100 dark:border-gray-800 my-px">
          <CardTitle className="text-xl text-purple-700 dark:text-gray-300 flex justify-between items-center flex-wrap gap-2">
            <span>Component Estimates</span>
            <ProjectActions
              isSaving={isSaving}
              isSendingEmail={isSendingEmail}
              onSave={onSaveEstimates}
              onSendEmail={onSendEmail}
              onExportPDF={onGeneratePDF}
              onExportExcel={onExportExcel}
            />
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 overflow-x-auto">
          <EstimationSummary 
            totalComponents={totals.total_points} 
            devDays={totals.total_dev_days} 
            totalDays={totals.total_days} 
          />

          <EstimationTable 
            estimates={rfpEstimates} 
            totals={totals} 
            onUpdateEstimate={onEstimateUpdate} 
          />
          
          <EstimationNotes />
          
          <div className="hidden">
            <div ref={reportRef} className="p-8 bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="mb-6 border-b pb-4">
                <h2 className="text-2xl font-bold text-purple-700">{project?.name}</h2>
                <p className="text-gray-600 mt-2">{project?.description || "No description provided"}</p>
                <div className="flex justify-between mt-4 text-sm">
                  <div>
                    <span>Date: <span className="font-semibold">{new Date().toLocaleDateString()}</span></span>
                    <span className="ml-4">Version: <span className="font-semibold">{project?.version}</span></span>
                  </div>
                  <span>Last Updated: <span className="font-semibold">{project?.updated_at ? new Date(project.updated_at).toLocaleDateString() : new Date().toLocaleDateString()}</span></span>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3 text-purple-600">Estimation Summary</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-purple-50 p-4 rounded-lg text-center">
                    <div className="text-sm text-gray-600">Total Components</div>
                    <div className="text-2xl font-bold text-purple-700">
                      {totals.total_points}
                    </div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg text-center">
                    <div className="text-sm text-gray-600">Dev Days</div>
                    <div className="text-2xl font-bold text-purple-700">
                      {totals.total_dev_days.toFixed(1)}
                    </div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg text-center">
                    <div className="text-sm text-gray-600">Total Days</div>
                    <div className="text-2xl font-bold text-purple-700">
                      {totals.total_days.toFixed(1)}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3 text-purple-600">Detailed Breakdown</h3>
                <table className="min-w-full bg-white border border-gray-200">
                  <thead>
                    <tr className="bg-purple-50">
                      <th className="p-2 text-left border border-gray-200">Component Type</th>
                      <th className="p-2 text-center border border-gray-200">Simple</th>
                      <th className="p-2 text-center border border-gray-200">Medium</th>
                      <th className="p-2 text-center border border-gray-200">Complex</th>
                      <th className="p-2 text-center border border-gray-200">Supercomplex</th>
                      <th className="p-2 text-center border border-gray-200">Total Components</th>
                      <th className="p-2 text-center border border-gray-200">Dev Days</th>
                      <th className="p-2 text-center border border-gray-200">Total Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedEstimates.filter(est => est.total_points > 0).map((estimate, index) => (
                      <tr key={index} className="border-b border-gray-200">
                        <td className="p-2 font-medium border border-gray-200">{estimate.component_type}</td>
                        <td className="p-2 text-center border border-gray-200">{estimate.simple_count}</td>
                        <td className="p-2 text-center border border-gray-200">{estimate.medium_count}</td>
                        <td className="p-2 text-center border border-gray-200">{estimate.complex_count}</td>
                        <td className="p-2 text-center border border-gray-200">{estimate.supercomplex_count}</td>
                        <td className="p-2 text-center font-semibold border border-gray-200">{estimate.total_points}</td>
                        <td className="p-2 text-center border border-gray-200">{estimate.total_dev_days.toFixed(1)}</td>
                        <td className="p-2 text-center border border-gray-200">{estimate.total_days.toFixed(1)}</td>
                      </tr>
                    ))}
                    <tr className="bg-purple-100">
                      <td className="p-2 font-bold border border-gray-200">TOTALS</td>
                      <td className="p-2 text-center font-semibold border border-gray-200">{totals.simple_count}</td>
                      <td className="p-2 text-center font-semibold border border-gray-200">{totals.medium_count}</td>
                      <td className="p-2 text-center font-semibold border border-gray-200">{totals.complex_count}</td>
                      <td className="p-2 text-center font-semibold border border-gray-200">{totals.supercomplex_count}</td>
                      <td className="p-2 text-center font-bold border border-gray-200">{totals.total_points}</td>
                      <td className="p-2 text-center font-bold border border-gray-200">{totals.total_dev_days.toFixed(1)}</td>
                      <td className="p-2 text-center font-bold border border-gray-200">{totals.total_days.toFixed(1)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EstimationTabContent;
